import { useDispatch, useSelector } from '@redux';
import crudActions from '@redux/crudActions';
import crudSelectors from '@redux/crudSelectors';
import { BreadcrumbProps } from 'antd';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import Breadcrumb from 'components/commons/Breadcrumb';
import RestFilter, { TRestFilterProps } from 'components/rest/Filter';
import RestTable, { IRestTableProps } from 'components/rest/Table';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import {
  useGetRootPath,
  useInitialFilters,
  useShowDefaultLayout,
  // useShowLayout,
} from 'hooks/useShowLayout';
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  DynamicObjectType,
  FilterFromUrlResponseType,
  TCommonDataType,
} from 'types/commons';
import { useShowColumns } from '../../../hooks/useShowLayout';
import CustomLayout from 'components/rest/CustomLayout';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { TLayoutItem } from '@redux/layouts/actions';
import { TFilterRequest } from 'components/rest/Filter/FilterContent';
import { useNavigate } from 'react-router-dom';
import FilterV2 from 'components/rest/FilterV2';
import { pick, values } from 'lodash';
import RestFilterForm from 'components/rest/RestFilterForm';
import { KEY_SEARCH_MORE } from 'constants/filters';
import { LabelValuePairsType } from 'core/types/common';
import { removeEmptyValueOfPathUrl } from 'utils/tools';

interface IRestListProps extends Omit<IRestTableProps, 'onChangePagination'> {
  model: string;
  breadcrumbs?: ItemType[];
  defaultOptions?: DynamicObjectType;
  initialFilter?: FilterFromUrlResponseType;
  hasDel?: boolean;
  hasShow?: boolean;
  hasEdit?: boolean;
  hasCustomLayout?: boolean;
  hasShowFilter?: boolean;
  filters?: TFilterItem[];
  positionKey?: string;
  hasHeader?: boolean;
  hasOnlyFilter?: boolean;
  hasImport?: boolean;
  hasExport?: boolean;
  hasFilterV2?: boolean;
  hasCreate?: boolean;
  hasSearch?: boolean;
  customActions?: ReactNode;
  gotoCreatePage?: () => void;
  placeholderInputSearch?: string;
  searchKeys?: string[];
  formatListData?: (data: DynamicObjectType[]) => DynamicObjectType[];
  dependency?: TCommonDataType;
  // eslint-disable-next-line
  customApiGetAll?: any;
  customLayout?: React.ReactElement;
  filterV2?: DynamicObjectType;
  filterCustomCPN?: React.ReactElement;
  formatFilter?: (values) => void;
  hasFilter?: boolean;
  hasCardComponent?: React.ReactElement;
  exportExcelQuery?: string;
  excelName?: string;
  isExportWithFilter?: boolean;
  initialLayouts?: TLayoutItem[];
  hasExportV2?: boolean;
  hasImportV2?: boolean;
  // eslint-disable-next-line
  importActions?: any;
  openModalImport?: () => void;
  openModalExport?: () => void;
  toolTipContent?: ReactNode | (() => ReactNode);
  dataFieldSearch?: LabelValuePairsType;
  keyFieldSearch?: string;
  extraActionsHeader?: ReactNode;
  searchOperator?: string;
  searchCondition?: string;
  listReft?: React.RefObject<HTMLDivElement>;
  ignoreDefault?: boolean;
  hasSubCustomLayout?: React.ReactElement;
}

const DUMMY = {};
const DUMMY_ARR = [];

const RestList: React.FC<
  IRestListProps & BreadcrumbProps & TRestFilterProps
> = ({
  initialFilter = DUMMY as FilterFromUrlResponseType,
  defaultOptions = DUMMY,
  columns = DUMMY_ARR,
  breadcrumbs = DUMMY_ARR,
  model,
  hasDel,
  hasShow,
  hasEdit,
  extraActions,
  filters,
  hasDraggable,
  positionKey,
  rowSelectionActions,
  hasHeader = true,
  hasOnlyFilter,
  hasImport,
  hasExport,
  hasCustomLayout = true,
  placeholderInputSearch = 'Search by name,slug...',
  hasFilterV2,
  hasCreate,
  hasSearch,
  customActions,
  gotoCreatePage,
  formatListData,
  searchKeys,
  hasShowFilter,
  dependency,
  customApiGetAll,
  customLayout,
  filterV2,
  filterCustomCPN,
  formatFilter,
  hasFilter = true,
  hasCardComponent,
  exportExcelQuery,
  excelName,
  isExportWithFilter,
  initialLayouts,
  hasExportV2,
  hasImportV2,
  openModalImport,
  openModalExport,
  hasToolTipExportButton,
  toolTipContent,
  dataFieldSearch,
  keyFieldSearch = 'fieldSearch',
  extraActionsHeader,
  searchOperator,
  searchCondition,
  listReft,
  customExport,
  hasSubCustomLayout,
  ...props
}) => {
  // const isMounted = useRef<boolean>();
  const listData = useSelector<{ id: string }[]>(
    crudSelectors[model].getDataArr,
  );

  const total = useSelector<number>(crudSelectors[model].getTotal);

  const filterData = useSelector<{ page: number; pageSize: number }>(
    crudSelectors[model].getFilters,
  );
  const idDataDel = useSelector<boolean>((state) => state[model].idDataDel);

  const loading = useSelector<boolean>(crudSelectors[model].getLoading);
  const { filter, searchParams, setSearchParams } = useGetFilter();
  const dispatch = useDispatch();
  const { rootPath, pathname, search } = useGetRootPath();
  const customLayouts = useShowDefaultLayout([
    {
      title: 'All',
      rootPath,
      customPath: rootPath,
      pathId: 'ALL',
      isDefault: true,
    },
    ...(initialLayouts || []),
  ]);
  const showColumns = useShowColumns(columns, props?.ignoreDefault);
  const initialFilterLayout = useInitialFilters();
  const navigate = useNavigate();

  const handleEditRow = useCallback((data, options) => {
    dispatch(
      crudActions[model].edit({
        data,
        options,
      }),
    );
  }, []);

  const gotoShowPage = (id) => {
    navigate(`${id}/show`);
  };

  const gotoEditPage = (id) => {
    navigate(`${id}/edit`);
  };
  const updatePosition = useCallback(
    (e) => {
      dispatch(
        crudActions[model].updatePosition({
          data: e,
          options: { positionKey },
        }),
      );
    },
    [dispatch],
  );

  const handleGetList = () => {
    const filterData = JSON.parse(
      JSON.stringify({
        ...pick(filter, KEY_SEARCH_MORE),
        q: filter?.q || initialFilter?.q,
        orderBy: filter?.orderBy || initialFilter?.orderBy,
        pageSize: filter?.pageSize || initialFilter?.pageSize,
        offset: filter?.offset || initialFilter?.offset,
        join: filter?.join || initialFilter?.join,
        page: filter?.page || initialFilter?.page,
        filter: initialFilter?.filter,
        filters: filter.filters
          ? [...values(initialFilter?.filters || {}), ...values(filter.filters)]
          : [
              ...values(initialFilterLayout?.filters || {}),
              ...values(initialFilter?.filters || {}),
            ],
        s: [],
        rawQuery: initialFilter?.rawQuery,
        outSideFilter: initialFilter?.outSideFilter,
      }),
    );
    if (searchKeys?.length && filterData.q) {
      searchKeys.forEach((key) => {
        filterData.s.push({
          key,
          value: filterData.q.toString().trim(),
          operator: searchOperator || 'contL',
          condition: searchCondition || 'or',
        });
      });
    }
    if (customApiGetAll) {
      dispatch(
        customApiGetAll({
          data: filterData,
          options: { ...defaultOptions, isRefresh: true },
        }),
      );
      return;
    }
    dispatch(
      crudActions[model].getAll({
        data: filterData,
        options: { ...defaultOptions, isRefresh: true },
      }),
    );
  };

  useEffect(() => {
    handleGetList();
  }, [filter, model, searchKeys, initialFilter, dependency, idDataDel]);

  useEffect(() => {
    // isMounted.current = true;
    const pathNameFormat = removeEmptyValueOfPathUrl(pathname);
    const customPath = initialFilterLayout?.customPath
      ? removeEmptyValueOfPathUrl(initialFilterLayout?.customPath)
      : null;

    if (!search && customPath && pathNameFormat !== customPath) {
      navigate(customPath, { replace: true });
    }
  }, []);

  const onChangePagination = ({ current, pageSize }, filters, sorter) => {
    if (sorter?.order) {
      searchParams.set(
        'orderBy',
        sorter?.order === 'ascend' ? `${sorter?.field}` : `-${sorter?.field}`,
      );
    } else {
      searchParams.delete('orderBy');
    }
    searchParams.set('page', current);
    searchParams.set('pageSize', pageSize);
    setSearchParams(searchParams);
  };

  const onChangeSearchQ = (q, fieldSearch) => {
    const valueTrim = q.trim();
    if (valueTrim !== '') {
      searchParams.set('q', q);
      searchParams.delete('page');
      searchParams.delete('pageSize');
    } else {
      searchParams.delete('q');
    }
    if (fieldSearch) {
      searchParams.set('fieldSearch', fieldSearch);
    } else {
      searchParams.delete('fieldSearch');
    }
    setSearchParams(searchParams);
  };

  const listDataFormatted = useMemo(() => {
    if (formatListData) {
      return (
        formatListData(listData).map((e) => ({ ...e, key: e.id, id: e.id })) ||
        []
      );
    }
    return listData;
  }, [listData, formatListData]);

  const retrieveList = (values: DynamicObjectType) => {
    searchParams.set('filters', JSON.stringify(values));
    setSearchParams(searchParams);
  };

  const initValueSelectSearch = useMemo(() => {
    return searchParams.get(keyFieldSearch || 'fieldSearch');
  }, [searchParams, keyFieldSearch]);

  return (
    <div>
      {hasHeader && !hasFilterV2 && (
        <>
          {!hasOnlyFilter && (
            <>
              {breadcrumbs.length > 0 && (
                <div className="flex-center-between">
                  <Breadcrumb items={breadcrumbs} />
                  <div>{extraActionsHeader}</div>
                </div>
              )}
              {hasCustomLayout && (
                <CustomLayout
                  items={customLayouts}
                  hasSubCustomLayout={hasSubCustomLayout}
                />
              )}
              {hasCustomLayout && hasSubCustomLayout}
            </>
          )}
          {hasFilter && (
            <RestFilter
              columns={columns}
              filters={filters}
              defaultValueQ={searchParams.get('q') || initialFilterLayout?.q}
              initFieldSearch={initValueSelectSearch}
              onChangeSearchQ={onChangeSearchQ}
              filterData={filter.filters as TFilterRequest[]}
              showColumns={showColumns}
              placeholder={placeholderInputSearch}
              hasImport={hasImport}
              hasExport={hasExport}
              hasShow={hasShow ?? hasShowFilter}
              extraActionFilters={props.extraActionFilters}
              exportExcelQuery={exportExcelQuery}
              model={model}
              excelName={excelName}
              isExportWithFilter={isExportWithFilter}
              filter={filter}
              hasExportV2={hasExportV2}
              hasImportV2={hasImportV2}
              openModalImport={openModalImport}
              openModalExport={openModalExport}
              hasToolTipExportButton={hasToolTipExportButton}
              toolTipContent={toolTipContent}
              dataFieldSearch={dataFieldSearch}
              customExport={customExport}
            />
          )}
        </>
      )}

      {filterCustomCPN && (
        <RestFilterForm
          format={formatFilter}
          filter={filter}
          retrieveList={retrieveList}
        >
          {filterCustomCPN}
        </RestFilterForm>
      )}

      {hasFilterV2 && (
        <FilterV2
          resource={model}
          resourceFilter={filter}
          customActions={customActions}
          gotoCreatePage={gotoCreatePage}
          hasCreate={hasCreate}
          hasSearch={hasSearch}
          hasExport={hasExport}
          exportExcelQuery={exportExcelQuery}
          {...filterV2}
        />
      )}

      {hasCardComponent}

      {customLayout ? (
        React.cloneElement(customLayout, {
          model,
          resourceData: listDataFormatted,
          resourceFilter: filterData,
          onChangePagination,
          handleEditRow,
        })
      ) : (
        <RestTable
          total={total}
          dataSource={listDataFormatted}
          loading={loading}
          columns={columns}
          model={model}
          key={'rest-table'}
          updatePosition={updatePosition}
          gotoShowPage={gotoShowPage}
          gotoEditPage={gotoEditPage}
          onChangePagination={onChangePagination}
          hasDel={hasDel}
          hasShow={hasShow}
          hasEdit={hasEdit}
          rowSelectionActions={rowSelectionActions}
          page={filterData.page}
          pageSize={filterData.pageSize}
          extraActions={extraActions}
          showColumns={showColumns}
          handleEditRow={handleEditRow}
          hasDraggable={hasDraggable}
          ref={listReft}
          {...props}
        />
      )}
    </div>
  );
};

export default RestList;
